import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { SharedProjectService } from 'src/app/core/services/shared-project.service';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { WorkSpace } from 'src/app/core/types';

@Component({
    selector: 'gl-loading-space-element',
    templateUrl: './loading-space-element.component.html',
    styleUrls: ['./loading-space-element.component.scss'],
    standalone: false
})
export class LoadingSpaceElementComponent implements AfterContentInit, OnDestroy {
  @Input() public workSpace: WorkSpace;
  @Input() public workSpaceIndex: number;
  @Input() public cargoSpaceIndex: number;
  @Input() @HostBinding('class.undoOpen') public isClosed: boolean;
  @Input() public palletQuantity = 1;
  @Output() public showLoads = new EventEmitter<number>();
  @Output() public palletQuantityChange = new EventEmitter<number>();
  @HostBinding('class.active') public edit = false;
  @HostBinding('class.currentWorkSpace') public isCurrentWorkSpace: boolean;
  @ViewChild('editTrigger', { static: false, read: ElementRef }) public editTriggerEl: ElementRef;
  @ViewChild('editwindow', { static: false, read: ElementRef })
  public editWindow: ElementRef<HTMLElement>;
  public isMobile: boolean;
  public isSharedView: boolean;
  private project: Project;
  private currentWorkSpaceIndex: number;
  private subscriptions: Subscription[] = [];
  private pendingQuantityDelta = 0;
  private quantityTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private renderer: Renderer2,
    private elRef: ElementRef<HTMLElement>,
    private projectService: ProjectService,
    private sharedProjectService: SharedProjectService,
    private router: Router,
  ) {
    this.isMobile = this.router.url.includes('mobile');
    this.subscriptions.push(
      this.projectService.project.subscribe((project: Project) => (this.project = project)),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe((currentWorkSpaceIndex) => {
        this.currentWorkSpaceIndex = currentWorkSpaceIndex;
        this.checkIsCurrentWorkSpace();
      }),
    );
    this.subscriptions.push(
      this.sharedProjectService.isViewingShared.subscribe((result) => (this.isSharedView = result)),
    );
  }

  public ngAfterContentInit(): void {
    this.checkIsCurrentWorkSpace();
  }

  public ngOnDestroy() {
    if (this.quantityTimer) {
      clearTimeout(this.quantityTimer);
    }
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public changeCurrentWorkSpace(index: number) {
    this.projectService.setState(this.project, index);
  }

  public onShowLoads(index: number, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showLoads.emit(index);
  }

  public get showPalletQuantityCounter(): boolean {
    return (
      this.cargoSpaceIndex === 0 &&
      this.workSpace?.cargoSpaces?.[0]?.type === LoadingSpaceType.Pallet &&
      this.palletQuantity > 1
    );
  }

  public displayedPalletQuantity(): number {
    return Math.max(1, this.palletQuantity + this.pendingQuantityDelta);
  }

  public incrementPalletQuantity(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.queuePalletQuantity(1);
  }

  public decrementPalletQuantity(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.queuePalletQuantity(-1);
  }

  private queuePalletQuantity(delta: number): void {
    if (this.isSharedView || this.isMobile) {
      return;
    }
    if (delta < 0 && this.displayedPalletQuantity() <= 1) {
      return;
    }
    this.pendingQuantityDelta += delta;
    if (this.quantityTimer) {
      clearTimeout(this.quantityTimer);
    }
    if (this.pendingQuantityDelta === 0) {
      this.quantityTimer = undefined;
      return;
    }
    this.quantityTimer = setTimeout(() => this.flushPalletQuantity(), 600);
  }

  private flushPalletQuantity(): void {
    this.quantityTimer = undefined;
    const delta = this.pendingQuantityDelta;
    this.pendingQuantityDelta = 0;
    if (!delta) {
      return;
    }
    this.palletQuantity = Math.max(1, this.palletQuantity + delta);
    this.palletQuantityChange.emit(delta);
  }

  public editLoadingSpace(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.edit = !this.edit;
    if (this.edit) {
      window.setTimeout(() => {
        this.renderer.setStyle(this.editWindow.nativeElement, 'left', '450px');
        this.renderer.setStyle(
          this.editWindow.nativeElement,
          'top',
          this.calculateEditWindowTopPosition() + 'px',
        );
        window.setTimeout(() => {
          this.renderer.setStyle(
            this.editWindow.nativeElement.children[0],
            'top',
            this.calculateEditWindowChildTopPosition() + 'px',
          );
          this.renderer.setStyle(this.editWindow.nativeElement, 'visibility', 'visible');
        }, 0);
      }, 0);
    }
  }

  public onCloseEditLoadingSpace() {
    this.edit = false;
  }

  private calculateEditWindowTopPosition(): number {
    const editWindowRect = this.editWindow.nativeElement.getBoundingClientRect();
    const listElementRect = this.elRef.nativeElement.getBoundingClientRect();
    const NAVBAR_HEIGHT = 50;
    let top = listElementRect.top - NAVBAR_HEIGHT;
    if (top + editWindowRect.height > window.innerHeight) {
      top = window.innerHeight - editWindowRect.height - NAVBAR_HEIGHT;
    }
    return top;
  }

  private calculateEditWindowChildTopPosition(): number {
    const editWindowRect = this.editWindow.nativeElement.getBoundingClientRect();
    const editWindowChildRect = this.editWindow.nativeElement.children[0].getBoundingClientRect();
    const listElementRect = this.elRef.nativeElement.getBoundingClientRect();
    let top = listElementRect.top - editWindowRect.top;
    if (editWindowChildRect.height > editWindowRect.height - top) {
      top = editWindowRect.height - editWindowChildRect.height;
    }
    return top;
  }

  public moreOpen(value: boolean): void {
    if (window.innerHeight <= 900 && value) {
      this.renderer.setStyle(this.editWindow.nativeElement, 'top', 0 + 'px');
    }
    if (window.innerHeight <= 900 && !value) {
      this.renderer.setStyle(
        this.editWindow.nativeElement,
        'top',
        this.calculateEditWindowTopPosition() + 'px',
      );
    }
  }

  private checkIsCurrentWorkSpace() {
    this.isCurrentWorkSpace = this.currentWorkSpaceIndex === this.workSpaceIndex;
  }
}
